import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CheckCircle,
  Clock,
  Mail,
  Shield,
  ShieldOff,
  Trash2,
  UserPlus,
  XCircle,
} from "lucide-react";
import { useId, useState } from "react";
import { toast } from "react-hot-toast";
import { trpc } from "@/app/api/trpcClient";
import { Button } from "@/app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/app/components/ui/sheet";
import { adminGuardHOC } from "@/app/hooks/useAdminGuard";

export const Route = createFileRoute("/admin/users")({
  component: adminGuardHOC(UsersManagement),
});

function UsersManagement() {
  const emailId = useId();
  const notesId = useId();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: users = [], refetch: refetchUsers } = trpc.users.list.useQuery();
  const { data: whitelist = [], refetch: refetchWhitelist } = trpc.users.getWhitelist.useQuery();
  const { data: me } = trpc.users.me.useQuery();

  const inviteUser = trpc.users.inviteUser.useMutation({
    onSuccess: () => {
      toast.success("User invited successfully!");
      setInviteModalOpen(false);
      setEmail("");
      setNotes("");
      void refetchWhitelist();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to invite user");
    },
  });

  const revokeInvitation = trpc.users.revokeInvitation.useMutation({
    onSuccess: () => {
      toast.success("Invitation revoked");
      void refetchWhitelist();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to revoke invitation");
    },
  });

  const deleteWhitelistEntry = trpc.users.deleteWhitelistEntry.useMutation({
    onSuccess: () => {
      toast.success("Whitelist entry deleted");
      void refetchWhitelist();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete entry");
    },
  });

  const setUserAdmin = trpc.users.setUserAdmin.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(variables.isAdmin ? "User promoted to admin" : "Admin access revoked");
      void refetchUsers();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update admin status");
    },
  });

  const handleInvite = async () => {
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }

    setIsSubmitting(true);
    try {
      await inviteUser.mutateAsync({ email, notes: notes || undefined });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "revoked":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1";
    const statusClasses = {
      active: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      revoked: "bg-red-100 text-red-700",
    };

    return (
      <span
        className={`${baseClasses} ${statusClasses[status as keyof typeof statusClasses] || ""}`}
      >
        {getStatusIcon(status)}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
          <p className="text-gray-600">Manage user access and invitations</p>
        </div>
        <Button onClick={() => setInviteModalOpen(true)} className="flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Active Users */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Active Users ({users.length})</CardTitle>
          <CardDescription>Users who have signed up and have access</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {users.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">No users yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-gray-500">
                      <th className="pb-2 font-medium">Name</th>
                      <th className="pb-2 font-medium">Email</th>
                      <th className="pb-2 font-medium">Type</th>
                      <th className="pb-2 font-medium">Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const isSelf = me?.id === user.id;
                      const isPending =
                        setUserAdmin.isPending && setUserAdmin.variables?.userId === user.id;
                      const isBootstrap = user.isBootstrapAdmin;
                      return (
                        <tr key={user.id} className="border-b last:border-0">
                          <td className="py-3">
                            <div className="flex items-center gap-3">
                              {user.image && (
                                <img
                                  src={user.image}
                                  alt={user.name}
                                  className="h-8 w-8 rounded-full"
                                />
                              )}
                              <span className="font-medium">{user.name}</span>
                              {user.isAdmin && (
                                <span
                                  className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1"
                                  title={isBootstrap ? "Bootstrap admin (AUTH_SITE_ADMINS)" : "Admin"}
                                >
                                  <Shield className="h-3 w-3" />
                                  Admin
                                  {isBootstrap && <span className="opacity-60">(env)</span>}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 text-gray-600">{user.email}</td>
                          <td className="py-3">
                            {user.email.endsWith("@comfy.org") ? (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                Comfy Org
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                External
                              </span>
                            )}
                          </td>
                          <td className="py-3">
                            {user.isAdmin ? (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isSelf || isBootstrap || isPending}
                                title={
                                  isSelf
                                    ? "You cannot revoke your own admin access"
                                    : isBootstrap
                                      ? "Bootstrap admin — set via AUTH_SITE_ADMINS env var, cannot be revoked from the UI"
                                      : "Revoke admin access"
                                }
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Revoke admin access for ${user.name} (${user.email})?`,
                                    )
                                  ) {
                                    void setUserAdmin.mutateAsync({
                                      userId: user.id,
                                      isAdmin: false,
                                    });
                                  }
                                }}
                                className="flex items-center gap-1 text-red-600 hover:text-red-700"
                              >
                                <ShieldOff className="h-3 w-3" />
                                Revoke
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isPending}
                                title="Grant admin access"
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Grant admin access to ${user.name} (${user.email})?`,
                                    )
                                  ) {
                                    void setUserAdmin.mutateAsync({
                                      userId: user.id,
                                      isAdmin: true,
                                    });
                                  }
                                }}
                                className="flex items-center gap-1"
                              >
                                <Shield className="h-3 w-3" />
                                Make Admin
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Email Whitelist */}
      <Card>
        <CardHeader>
          <CardTitle>Email Whitelist ({whitelist.length})</CardTitle>
          <CardDescription>Invited external users and their invitation status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {whitelist.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">No invitations sent yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-gray-500">
                      <th className="pb-2 font-medium">Email</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Invited</th>
                      <th className="pb-2 font-medium">Signed Up</th>
                      <th className="pb-2 font-medium">Notes</th>
                      <th className="pb-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {whitelist.map((entry) => (
                      <tr key={entry.id} className="border-b last:border-0">
                        <td className="py-3 font-medium">{entry.email}</td>
                        <td className="py-3">{getStatusBadge(entry.status)}</td>
                        <td className="py-3 text-sm text-gray-600">
                          {new Date(entry.invitedAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 text-sm text-gray-600">
                          {entry.signedUpAt ? new Date(entry.signedUpAt).toLocaleDateString() : "-"}
                        </td>
                        <td className="py-3 text-sm text-gray-600">{entry.notes || "-"}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            {entry.status === "pending" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Are you sure you want to revoke the invitation for ${entry.email}?`,
                                    )
                                  ) {
                                    void revokeInvitation.mutateAsync({
                                      email: entry.email,
                                    });
                                  }
                                }}
                                className="flex items-center gap-1"
                              >
                                <XCircle className="h-3 w-3" />
                                Revoke
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (
                                  confirm(
                                    `Are you sure you want to delete ${entry.email} from the whitelist?`,
                                  )
                                ) {
                                  void deleteWhitelistEntry.mutateAsync({
                                    email: entry.email,
                                  });
                                }
                              }}
                              className="flex items-center gap-1 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="mt-8">
        <Link
          to="/admin"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <title>Back arrow</title>
            <path
              fillRule="evenodd"
              d="M7.707 14.707a1 1 0 01-1.414 0L3.586 12l2.707-2.707a1 1 0 011.414 1.414L6.414 12l1.293 1.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
            <path
              fillRule="evenodd"
              d="M3 12a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Back to Admin Dashboard
        </Link>
      </div>

      {/* Invite Modal */}
      <Sheet open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Invite External User</SheetTitle>
            <SheetDescription>
              Send an invitation to an external collaborator to access Team Dash. They will receive
              an email with signup instructions.
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 py-4 px-4">
            <div className="grid gap-2">
              <Label htmlFor={emailId}>Email Address</Label>
              <Input
                id={emailId}
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={notesId}>Notes (optional)</Label>
              <Input
                id={notesId}
                placeholder="Add a note about this invitation..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <SheetFooter>
            <Button
              variant="outline"
              onClick={() => setInviteModalOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Send Invitation
                </>
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
